/**
 * The three career-milestone screens a sortie can end on: the medal citation,
 * the promotion card and the BOARD OF INQUIRY.
 *
 * All three are text screens the original draws out of a file. `MEDAL.DAT` holds
 * the twelve citations, `BOI.DAT` the board's accusation and its three graded
 * findings, and `PS.EXE` holds the promotion's one sentence — *"You are promoted
 * to %r."* — plus the citation screen's `CONGRATULATIONS!!` header, the board's
 * `BOARD OF INQUIRY` header, and the `CONTINUE` both end on. Evidence:
 * `docs/formats/screen-text-dat.md`, `docs/game/end-of-mission.md`.
 *
 * They are built on `debrief-screens.ts`'s machinery — a panel, a raised plate, a
 * prose block, a one-button action row — because that is what the two screens in
 * this family that *do* have captures (`mission-debriefing.png`,
 * `safe-return.png`) are. The plate is `safe-return.png`'s own rect, borrowed
 * rather than re-invented (`CEREMONY_PLATE`).
 *
 * **Three things here are port-side, and each is marked at the line that makes
 * the choice.** Nothing in this project has a capture of any of the three
 * screens, so their geometry is chosen rather than measured; the promotion card
 * has no title string anywhere in `PS.EXE`; and the citation's prose is
 * **re-flowed** rather than drawn on the file's own line breaks.
 *
 * The re-flow is the one worth arguing. `REVIEW.DAT`'s stored runs are provably
 * not screen rows — `mission-debriefing.png` breaks the same paragraph in
 * different places — and `MEDAL.DAT`'s cannot be either, because its lines are
 * authored around unexpanded tokens: `%n` is two characters and a pilot name is
 * up to twenty, `%w` is two characters and the decree line is thirty-two. Drawn
 * as stored, *"aircraft, %n is awarded Prussia's highest military"* runs off the
 * right edge of a 320-pixel screen for any pilot with a long name. So the joined
 * paragraph is wrapped to a measured column, exactly as the debriefing's is, and
 * `ceremony-screens.test.ts` measures the worst case against the real baked
 * atlas rather than trusting the arithmetic.
 */

import type { ConsequenceSection } from "../../career/career-view.ts";
import { SIMEND_PLATE } from "./simend-screens.ts";
import type { Rect } from "../render/draw-ops.ts";
import type { FontAtlas } from "../render/text-metrics.ts";
import type { MenuAction } from "./menu-model.ts";
import { actionRow, wrapText } from "./panel-parts.ts";
import type { ShellScreen, Widget } from "./shell-screen.ts";

/** `MEDALS.BMP`, whose first twelve frames are `MEDAL.DAT`'s twelve medals in order. */
export const MEDAL_SHEET = "sprites/MEDALS.BMP";

/**
 * The raised plate all three ceremonies are drawn on.
 *
 * Deliberately **the same rect** `safe-return.png` measures, borrowed rather
 * than re-invented: that capture is the one screen of exactly this shape the
 * project has — a titled panel carrying a short text and a picture — so its
 * plate is the nearest thing to evidence these three have. Bare panel bodies
 * looked like a missing screen when the browser check drew them.
 */
export const CEREMONY_PLATE: Rect = SIMEND_PLATE;

/** Port-side: a sunken well big enough for the largest frame in the sheet (32×39). */
export const MEDAL_WELL: Rect = { x: 45, y: 38, w: 40, h: 46 };
const MEDAL_PICTURE = { x: 49, y: 42 } as const;

/**
 * Port-side geometry, inset into `CEREMONY_PLATE` by roughly what
 * `safe-return.png`'s own line is inset into its plate (+7, +5). `column` keeps
 * the longest wrapped row inside the plate's right edge — the worst of the
 * twelve citations is eight rows, which clears the plate's bottom — and `pitch`
 * is the 9 rows `panel-parts.ts` documents for FONT3.
 */
const CITATION_PROSE = { x: 45, y: 94, pitch: 9, column: 230 } as const;

/** The promotion card and the board have no picture, so their prose starts at the top of the plate. */
const TOP_PROSE = { x: 40, y: 33, pitch: 9, column: 240 } as const;

/** Rows between the board's accusation and its finding. Port-side, like the rest of this geometry. */
const BOARD_GAP = 20;

/** Verbatim `PS.EXE`, and the citation screen's own header. */
export const CITATION_TITLE = "CONGRATULATIONS!!";

/** Verbatim `PS.EXE`, and the board's own header. */
export const BOARD_TITLE = "BOARD OF INQUIRY";
/** `BOARD.SNG`, as `MUSIC_PATHS` keys it (`shell/audio/shell-audio.ts`). */
export const BOARD_MUSIC = "BOARD";

export type CeremonyEmblem = "roundel" | "cross";

/**
 * The screen one consequence step wants, or nothing when it is an ordinary
 * popup step.
 *
 * This is the whole seam between the between-missions flow and these three
 * screens: `career-surface.ts` asks each step for a screen and paints one if it
 * gets one, and the step machine — which step is on, what CONTINUE does — is
 * untouched. A ceremony step therefore takes exactly the same one press to get
 * past that its popup did.
 */
export function ceremonyScreen(
  section: ConsequenceSection,
  nation: "german" | "allied",
  font: FontAtlas,
  heading: FontAtlas,
  action: MenuAction,
): ShellScreen | undefined {
  const spec = section.ceremony;
  if (!spec) return undefined;
  const emblem: CeremonyEmblem = nation === "german" ? "cross" : "roundel";
  if (spec.kind === "citation") {
    return citationScreen({ lines: section.lines, picture: spec.picture, emblem }, font, heading, action);
  }
  if (spec.kind === "promotion") {
    return promotionScreen(section.lines.join(" "), spec.rank, emblem, font, heading, action);
  }
  const [accusation, ...finding] = section.lines;
  return boardOfInquiryScreen(accusation ?? "", finding, emblem, font, heading, action);
}

function proseWidgets(
  lines: readonly string[],
  layout: { readonly x: number; readonly y: number; readonly pitch: number; readonly column: number },
  font: FontAtlas,
): Widget {
  return {
    kind: "prose",
    block: {
      lines: wrapText(font, lines.join(" "), layout.column),
      x: layout.x,
      y: layout.y,
      // FONT3 on a plate face: the emboss is computed against the plate these
      // three screens are drawn on, not against the panel's flat body.
      role: "label",
      pitch: layout.pitch,
    },
  };
}

export interface CitationScreen {
  /** `MEDAL.DAT`'s citation with its five tokens filled. */
  readonly lines: readonly string[];
  /** Which `MEDALS.BMP` frame this medal is, or nothing when the port has no picture for it. */
  readonly picture: number | undefined;
  readonly emblem: CeremonyEmblem;
}

/** `CONGRATULATIONS!!` over the medal and its citation. */
export function citationScreen(citation: CitationScreen, font: FontAtlas, heading: FontAtlas, action: MenuAction): ShellScreen {
  const picture: Widget[] =
    citation.picture === undefined
      ? []
      : [
          { kind: "well", rect: MEDAL_WELL },
          { kind: "picture", sheet: MEDAL_SHEET, frame: citation.picture, x: MEDAL_PICTURE.x, y: MEDAL_PICTURE.y },
        ];
  return {
    id: "career/citation",
    chrome: { kind: "panel", title: CITATION_TITLE, emblem: citation.emblem, background: undefined },
    widgets: [
      { kind: "plate", rect: CEREMONY_PLATE },
      ...picture,
      proseWidgets(citation.lines, CITATION_PROSE, font),
      ...actionRow([{ id: "continue", label: "CONTINUE", action }], heading, 178),
    ],
    cancel: action,
  };
}

/**
 * *"You are promoted to %r."*, after the salute.
 *
 * **The title is port-side.** `PS.EXE` holds exactly one promotion string and no
 * header to go with it — the block reads `b-sal.ttm`, `g-sal.ttm`, `You are
 * promoted to %r.`, and then `AIRCRAFT INTRODUCTION`, which is the *next*
 * screen's title. The medal ceremony three strings away does have its header
 * (`CONGRATULATIONS!!`), so the absence here is a real absence and not a gap in
 * the dump. Rather than borrow the medal's header or leave the band empty, the
 * card is titled with the rank the pilot has just been given, which is the one
 * word on this screen that is certainly his.
 */
export function promotionScreen(
  message: string,
  rank: string,
  emblem: CeremonyEmblem,
  font: FontAtlas,
  heading: FontAtlas,
  action: MenuAction,
): ShellScreen {
  return {
    id: "career/promotion",
    chrome: { kind: "panel", title: rank.toUpperCase(), emblem, background: undefined },
    widgets: [
      { kind: "plate", rect: CEREMONY_PLATE },
      proseWidgets([message], TOP_PROSE, font),
      ...actionRow([{ id: "continue", label: "CONTINUE", action }], heading, 178),
    ],
    cancel: action,
  };
}

/**
 * `BOARD OF INQUIRY`: the accusation, then the finding this offence earned.
 *
 * The port's own *"Strike 2 of 3."* line is deliberately not here. `BOI.DAT`
 * holds four texts and the board says exactly those; the strike count is
 * bookkeeping the original leaves to the escalation in the wording itself —
 * *"since it is your first offense"*, *"if it happens again"*.
 */
export function boardOfInquiryScreen(
  accusation: string,
  finding: readonly string[],
  emblem: CeremonyEmblem,
  font: FontAtlas,
  heading: FontAtlas,
  action: MenuAction,
): ShellScreen {
  return {
    id: "career/board-of-inquiry",
    // `PS.EXE` names `board.sng` beside `boi.dat`, the file this screen's text
    // comes from, and no `.TTM` in the corpus is a hearing scene — so the
    // hearing's own theme rides the screen rather than a cutscene.
    music: BOARD_MUSIC,
    chrome: { kind: "panel", title: BOARD_TITLE, emblem, background: undefined },
    widgets: [
      { kind: "plate", rect: CEREMONY_PLATE },
      proseWidgets([accusation], TOP_PROSE, font),
      proseWidgets(finding, { ...TOP_PROSE, y: TOP_PROSE.y + BOARD_GAP }, font),
      ...actionRow([{ id: "continue", label: "CONTINUE", action }], heading, 178),
    ],
    cancel: action,
  };
}
